import React, { useState, type JSX } from 'react'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { tomorrow } from 'react-syntax-highlighter/dist/cjs/styles/prism'

import './CreateCommandForm.css'

import { type Outcome } from '../../../base/Outcome'

import { CreateCommand } from '../../../Foobara/Commandforge/CreateCommand'

import type CreateCommandInputs from '../../../Foobara/Commandforge/CreateCommand/Inputs'

import type CreateCommandResult from '../../../Foobara/Commandforge/CreateCommand/Result'
import { type Error as CreateCommandError } from '../../../Foobara/Commandforge/CreateCommand/Errors'

import { type model_enum } from '../../../Foobara/Ai/AnswerBot/Types/model_enum'
import useQuery from '../../../hooks/useQuery'
import { GetCurrentUser } from '../../../Foobara/Commandforge'

import buildExampleScript from './buildExampleScript'
import buildGemfile from './buildGemfile'
import buildEnvLocal from './buildEnvLocal'

export default function CreateCommandForm (): JSX.Element {
  const [organizationName, setOrganizationName] = useState<string | undefined>(undefined)
  const [domainName, setDomainName] = useState<string | undefined>(undefined)
  const [commandName, setCommandName] = useState<string | undefined>(undefined)
  const [description, setDescription] = useState<string | null | undefined>(undefined)
  const [inputsType, setInputsType] = useState<string | undefined>(undefined)
  const [resultType, setResultType] = useState<string | undefined>(undefined)
  const [defaultLlmModel, setDefaultLlmModel] = useState<model_enum >('qwen3-coder:30b')

  const [result, setResult] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showDemos, setShowDemos] = useState<boolean>(false)
  const [copied, setCopied] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<'script' | 'gemfile' | 'envlocal'>('script')

  const { result: currentUser } = useQuery<GetCurrentUser>(GetCurrentUser, undefined)

  interface DemoSettings {
    organizationName?: string | undefined
    domainName: string
    commandName: string
    description: string
    inputsType: string
    resultType: string
    defaultLlmModel: model_enum
  }

  const computeDemoData: DemoSettings = {
    organizationName: 'CozeDiscord',
    domainName: 'WeeklyDemo',
    commandName: 'Compute',
    description: 'takes two operands and an operator and performs the computation.',
    inputsType: `operand1: integer
operand2: integer
operator:
  type: symbol
  one_of: ["+", "-", "*", "/", "**"]`,
    resultType: 'integer',
    defaultLlmModel: 'qwen3-coder:30b'
  }

  const determineRelativeDateDemoData: DemoSettings = {
    organizationName: 'CozeDiscord',
    domainName: 'WeeklyDemo',
    commandName: 'DetermineRelativeDate',
    description: 'Parses a relative date phrase and returns the corresponding date.',
    inputsType: `type: attributes
element_type_declarations:
  phrase:
    type: string
    description: A phrase describing a date relative to today in natural language
  today: date
required: [phrase, today]`,
    resultType: 'date',
    defaultLlmModel: 'claude-3-7-sonnet-20250219'
  }

  const analyzeSentenceDemoData: DemoSettings = {
    organizationName: 'CozeDiscord',
    domainName: 'WeeklyDemo',
    commandName: 'AnalyzeSentence',
    description: 'Accepts a sentence and analyzes and spell-corrects it',
    inputsType: `type: :attributes
element_type_declarations:
  sentence:
    description: The sentence to analyze and correct
    type: :string
required: [sentence]`,
    resultType: 'CozeDiscord::WeeklyDemo::AnalyzedSentence',
    defaultLlmModel: 'qwen3-coder:30b'
  }

  function organizationNameFromUsername (): string {
    if (currentUser?.username != null) {
      const normalized = currentUser.username.replaceAll(/[^\w\d]/g, ' ')
      let parts = normalized.split(/\s+/g)

      parts = parts.map((part) => {
        const s = part[0].toUpperCase()
        return s + part.substring(1)
      })

      return parts.join('')
    } else {
      return 'Loading...'
    }
  }

  function toKebabCase (str: string): string {
    return str
      .replace(/([a-z])([A-Z])/g, '$1-$2')
      .replace(/[\s_]+/g, '-')
      .toLowerCase()
  }

  function loadDemo (demoSettings: DemoSettings): void {
    let organizationName = demoSettings.organizationName

    if (organizationName == null) {
      organizationName = organizationNameFromUsername()
    }

    setOrganizationName(organizationName)
    setDomainName(demoSettings.domainName)
    setCommandName(demoSettings.commandName)
    setDescription(demoSettings.description)
    setInputsType(demoSettings.inputsType)
    setResultType(demoSettings.resultType)
    setDefaultLlmModel(demoSettings.defaultLlmModel)
  }

  const loadComputeDemo = (): void => { loadDemo(computeDemoData) }
  const loadDetermineRelativeDateDemo = (): void => { loadDemo(determineRelativeDateDemoData) }
  const loadAnalyzeSentenceDemo = (): void => { loadDemo(analyzeSentenceDemoData) }

  const copyToClipboard = async (): Promise<void> => {
    let textToCopy: string

    if (activeTab === 'script') {
      if (organizationName == null || domainName == null || commandName == null) {
        return
      }
      textToCopy = buildExampleScript({ organizationName, domainName, commandName })
    } else if (activeTab === 'gemfile') {
      textToCopy = buildGemfile()
    } else {
      textToCopy = buildEnvLocal()
    }

    await navigator.clipboard.writeText(textToCopy)
    setCopied(true)
    setTimeout(() => { setCopied(false) }, 2000)
  }

  function toVoid (fn: () => Promise<void>): () => void {
    return (): void => {
      void (async (): Promise<void> => { await fn() })()
    }
  }

  const run = toVoid(async (): Promise<void> => {
    if (organizationName == null) {
      // TODO: perform some kind of validation error
      return
    }

    if (domainName == null) {
      // TODO: perform some kind of validation error
      return
    }

    if (commandName == null) {
      // TODO: perform some kind of validation error
      return
    }

    if (description == null) {
      // TODO: perform some kind of validation error
      return
    }

    if (inputsType == null) {
      // TODO: perform some kind of validation error
      return
    }

    if (resultType == null) {
      // TODO: perform some kind of validation error
      return
    }

    if (defaultLlmModel == null) {
      // TODO: perform some kind of validation error
      return
    }

    const inputs: CreateCommandInputs = {
      organization_name: organizationName,
      domain_name: domainName,
      command_name: commandName,
      description,
      inputs_type: inputsType,
      result_type: resultType,
      default_llm_model: defaultLlmModel
    }

    const command = new CreateCommand(inputs)

    try {
      setResult('Thinking...')
      setError(null)
      const outcome: Outcome<CreateCommandResult, CreateCommandError> = await command.run()

      if (outcome.isSuccess()) {
        const result: CreateCommandResult = outcome.result
        setResult(typeof result === 'string' ? result : JSON.stringify(result))
      } else {
        setError(outcome.errorMessage)
        setResult(null)
      }
    } catch (error) {
      setError(`Error executing command: ${JSON.stringify(error)}`)
      setResult(null)
    }
  })

  return (
    <div className="CommandForm">
      <div>
        <div className="form-row">
          <input
            value={organizationName ?? ''}
            onChange={(e) => { setOrganizationName(e.target.value) }}
            placeholder="organization name"
          />

          <input
            value={domainName ?? ''}
            onChange={(e) => { setDomainName(e.target.value) }}
            placeholder="domain name"
          />

          <input
            value={commandName ?? ''}
            onChange={(e) => { setCommandName(e.target.value) }}
            placeholder="command name"
          />
        </div>

        <textarea
          className="description-field"
          value={description ?? ''}
          onChange={(e) => { setDescription(e.target.value as string | null) }}
          placeholder="description"
          rows={1}
        />

        <textarea
          value={inputsType ?? ''}
          onChange={(e) => { setInputsType(e.target.value) }}
          placeholder="inputs type"
          rows={8}
        />

        <textarea
          value={resultType ?? ''}
          onChange={(e) => { setResultType(e.target.value) }}
          placeholder="result type"
          rows={8}
        />

        <div className="form-row">
          <select
            value={defaultLlmModel ?? ''}
            onChange={(e) => { setDefaultLlmModel(e.target.value as model_enum) }}
          >
            <option value="babbage-002">babbage-002</option><option value="chatgpt-4o-latest">chatgpt-4o-latest</option><option value="claude-3-5-haiku-20241022">claude-3-5-haiku-20241022</option><option value="claude-3-7-sonnet-20250219">claude-3-7-sonnet-20250219</option><option value="claude-3-haiku-20240307">claude-3-haiku-20240307</option><option value="claude-3-opus-20240229">claude-3-opus-20240229</option><option value="claude-haiku-4-5-20251001">claude-haiku-4-5-20251001</option><option value="claude-opus-4-1-20250805">claude-opus-4-1-20250805</option><option value="claude-opus-4-20250514">claude-opus-4-20250514</option><option value="claude-sonnet-4-20250514">claude-sonnet-4-20250514</option><option value="claude-sonnet-4-5-20250929">claude-sonnet-4-5-20250929</option><option value="codellama:34b">codellama:34b</option><option value="codex-mini-latest">codex-mini-latest</option><option value="dall-e-2">dall-e-2</option><option value="dall-e-3">dall-e-3</option><option value="davinci-002">davinci-002</option><option value="deepseek-r1:1.5b">deepseek-r1:1.5b</option><option value="deepseek-r1:14b">deepseek-r1:14b</option><option value="deepseek-r1:32b">deepseek-r1:32b</option><option value="deepseek-r1:7b">deepseek-r1:7b</option><option value="deepseek-r1:8b">deepseek-r1:8b</option><option value="gpt-3.5-turbo">gpt-3.5-turbo</option><option value="gpt-3.5-turbo-0125">gpt-3.5-turbo-0125</option><option value="gpt-3.5-turbo-1106">gpt-3.5-turbo-1106</option><option value="gpt-3.5-turbo-16k">gpt-3.5-turbo-16k</option><option value="gpt-3.5-turbo-instruct">gpt-3.5-turbo-instruct</option><option value="gpt-3.5-turbo-instruct-0914">gpt-3.5-turbo-instruct-0914</option><option value="gpt-4">gpt-4</option><option value="gpt-4-0125-preview">gpt-4-0125-preview</option><option value="gpt-4-0613">gpt-4-0613</option><option value="gpt-4-1106-preview">gpt-4-1106-preview</option><option value="gpt-4-turbo">gpt-4-turbo</option><option value="gpt-4-turbo-2024-04-09">gpt-4-turbo-2024-04-09</option><option value="gpt-4-turbo-preview">gpt-4-turbo-preview</option><option value="gpt-4.1">gpt-4.1</option><option value="gpt-4.1-2025-04-14">gpt-4.1-2025-04-14</option><option value="gpt-4.1-mini">gpt-4.1-mini</option><option value="gpt-4.1-mini-2025-04-14">gpt-4.1-mini-2025-04-14</option><option value="gpt-4.1-nano">gpt-4.1-nano</option><option value="gpt-4.1-nano-2025-04-14">gpt-4.1-nano-2025-04-14</option><option value="gpt-4o">gpt-4o</option><option value="gpt-4o-2024-05-13">gpt-4o-2024-05-13</option><option value="gpt-4o-2024-08-06">gpt-4o-2024-08-06</option><option value="gpt-4o-2024-11-20">gpt-4o-2024-11-20</option><option value="gpt-4o-audio-preview">gpt-4o-audio-preview</option><option value="gpt-4o-audio-preview-2024-10-01">gpt-4o-audio-preview-2024-10-01</option><option value="gpt-4o-audio-preview-2024-12-17">gpt-4o-audio-preview-2024-12-17</option><option value="gpt-4o-audio-preview-2025-06-03">gpt-4o-audio-preview-2025-06-03</option><option value="gpt-4o-mini">gpt-4o-mini</option><option value="gpt-4o-mini-2024-07-18">gpt-4o-mini-2024-07-18</option><option value="gpt-4o-mini-audio-preview">gpt-4o-mini-audio-preview</option><option value="gpt-4o-mini-audio-preview-2024-12-17">gpt-4o-mini-audio-preview-2024-12-17</option><option value="gpt-4o-mini-realtime-preview">gpt-4o-mini-realtime-preview</option><option value="gpt-4o-mini-realtime-preview-2024-12-17">gpt-4o-mini-realtime-preview-2024-12-17</option><option value="gpt-4o-mini-search-preview">gpt-4o-mini-search-preview</option><option value="gpt-4o-mini-search-preview-2025-03-11">gpt-4o-mini-search-preview-2025-03-11</option><option value="gpt-4o-mini-transcribe">gpt-4o-mini-transcribe</option><option value="gpt-4o-mini-tts">gpt-4o-mini-tts</option><option value="gpt-4o-realtime-preview">gpt-4o-realtime-preview</option><option value="gpt-4o-realtime-preview-2024-10-01">gpt-4o-realtime-preview-2024-10-01</option><option value="gpt-4o-realtime-preview-2024-12-17">gpt-4o-realtime-preview-2024-12-17</option><option value="gpt-4o-realtime-preview-2025-06-03">gpt-4o-realtime-preview-2025-06-03</option><option value="gpt-4o-search-preview">gpt-4o-search-preview</option><option value="gpt-4o-search-preview-2025-03-11">gpt-4o-search-preview-2025-03-11</option><option value="gpt-4o-transcribe">gpt-4o-transcribe</option><option value="gpt-4o-transcribe-diarize">gpt-4o-transcribe-diarize</option><option value="gpt-5">gpt-5</option><option value="gpt-5-2025-08-07">gpt-5-2025-08-07</option><option value="gpt-5-chat-latest">gpt-5-chat-latest</option><option value="gpt-5-codex">gpt-5-codex</option><option value="gpt-5-mini">gpt-5-mini</option><option value="gpt-5-mini-2025-08-07">gpt-5-mini-2025-08-07</option><option value="gpt-5-nano">gpt-5-nano</option><option value="gpt-5-nano-2025-08-07">gpt-5-nano-2025-08-07</option><option value="gpt-5-pro">gpt-5-pro</option><option value="gpt-5-pro-2025-10-06">gpt-5-pro-2025-10-06</option><option value="gpt-5-search-api">gpt-5-search-api</option><option value="gpt-5-search-api-2025-10-14">gpt-5-search-api-2025-10-14</option><option value="gpt-audio">gpt-audio</option><option value="gpt-audio-2025-08-28">gpt-audio-2025-08-28</option><option value="gpt-audio-mini">gpt-audio-mini</option><option value="gpt-audio-mini-2025-10-06">gpt-audio-mini-2025-10-06</option><option value="gpt-image-1">gpt-image-1</option><option value="gpt-image-1-mini">gpt-image-1-mini</option><option value="gpt-realtime">gpt-realtime</option><option value="gpt-realtime-2025-08-28">gpt-realtime-2025-08-28</option><option value="gpt-realtime-mini">gpt-realtime-mini</option><option value="gpt-realtime-mini-2025-10-06">gpt-realtime-mini-2025-10-06</option><option value="llama3.1:8b">llama3.1:8b</option><option value="llama3.2:3b">llama3.2:3b</option><option value="mistral:7b">mistral:7b</option><option value="mixtral:8x7b">mixtral:8x7b</option><option value="o1">o1</option><option value="o1-2024-12-17">o1-2024-12-17</option><option value="o1-mini">o1-mini</option><option value="o1-mini-2024-09-12">o1-mini-2024-09-12</option><option value="o1-pro">o1-pro</option><option value="o1-pro-2025-03-19">o1-pro-2025-03-19</option><option value="o3">o3</option><option value="o3-2025-04-16">o3-2025-04-16</option><option value="o3-mini">o3-mini</option><option value="o3-mini-2025-01-31">o3-mini-2025-01-31</option><option value="o4-mini">o4-mini</option><option value="o4-mini-2025-04-16">o4-mini-2025-04-16</option><option value="o4-mini-deep-research">o4-mini-deep-research</option><option value="o4-mini-deep-research-2025-06-26">o4-mini-deep-research-2025-06-26</option><option value="omni-moderation-2024-09-26">omni-moderation-2024-09-26</option><option value="omni-moderation-latest">omni-moderation-latest</option><option value="phi3.5:3.8b">phi3.5:3.8b</option><option value="phi3:3.8b">phi3:3.8b</option><option value="phi4-mini:3.8b">phi4-mini:3.8b</option><option value="phi4-reasoning:14b">phi4-reasoning:14b</option><option value="phi4:14b">phi4:14b</option><option value="qwen3-coder:30b">qwen3-coder:30b</option><option value="qwen3:0.6b">qwen3:0.6b</option><option value="qwen3:1.7b">qwen3:1.7b</option><option value="qwen3:14b">qwen3:14b</option><option value="qwen3:30b">qwen3:30b</option><option value="qwen3:32b">qwen3:32b</option><option value="qwen3:4b">qwen3:4b</option><option value="qwen3:8b">qwen3:8b</option><option value="sora-2">sora-2</option><option value="sora-2-pro">sora-2-pro</option><option value="text-embedding-3-large">text-embedding-3-large</option><option value="text-embedding-3-small">text-embedding-3-small</option><option value="text-embedding-ada-002">text-embedding-ada-002</option><option value="tts-1">tts-1</option><option value="tts-1-1106">tts-1-1106</option><option value="tts-1-hd">tts-1-hd</option><option value="tts-1-hd-1106">tts-1-hd-1106</option><option value="whisper-1">whisper-1</option>
          </select>

          <button onClick={run}>Create LlmBackedCommand</button>
        </div>

        <div className="demo-section">
          <button
            onClick={() => { setShowDemos(!showDemos) }}
            className="demo-toggle-button"
          >
            {showDemos ? '▼' : '▶'} Demo examples
          </button>
          {showDemos && (
            <div className="demo-container">
              <button onClick={loadComputeDemo} className="demo-button">Compute Demo</button>
              <button onClick={loadDetermineRelativeDateDemo} className="demo-button">DetermineRelativeDate Demo</button>
              <button onClick={loadAnalyzeSentenceDemo} className="demo-button">AnalyzeSentence Demo</button>
            </div>
          )}
        </div>
      </div>

      {(result != null && organizationName != null && domainName != null && commandName != null) && <>
        <p>
          Your command is ready! You can run it a number of ways. Here&apos;s an example Ruby script:
        </p>
        <div>
          <div className="tab-container">
            <button
              onClick={() => { setActiveTab('script') }}
              className={`tab-button ${activeTab === 'script' ? 'active' : ''}`}
            >
              {domainName != null ? toKebabCase(domainName) : 'example_script.rb'}
            </button>
            <button
              onClick={() => { setActiveTab('gemfile') }}
              className={`tab-button ${activeTab === 'gemfile' ? 'active' : ''}`}
            >
              Gemfile
            </button>
            <button
              onClick={() => { setActiveTab('envlocal') }}
              className={`tab-button ${activeTab === 'envlocal' ? 'active' : ''}`}
            >
              .env.local
            </button>
          </div>
          <div className="code-container">
            <button
              onClick={() => { void copyToClipboard() }}
              className={`copy-button ${copied ? 'copied' : ''}`}
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
            {activeTab === 'script' && (
              <SyntaxHighlighter language="ruby" style={tomorrow}>
                {buildExampleScript({ organizationName, domainName, commandName })}
              </SyntaxHighlighter>
            )}
            {activeTab === 'gemfile' && (
              <SyntaxHighlighter language="ruby" style={tomorrow}>
                {buildGemfile()}
              </SyntaxHighlighter>
            )}
            {activeTab === 'envlocal' && (
              <SyntaxHighlighter language="bash" style={tomorrow}>
                {buildEnvLocal()}
              </SyntaxHighlighter>
            )}
          </div>
        </div>
      </>}
      {(error != null) && <p className="error-message">{error}</p>}
    </div>
  )
}
